import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import type { PreferenceCardListItemEntity } from '@peritasai/repository';
import { DataTable } from '@peritasai/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (
  t: (key: string) => string
): GridColDef<PreferenceCardListItemEntity>[] => [
  {
    field: 'surgeonName',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnSurgeonName'),
    flex: 1.5,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'caseTypeDisplayName',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnCaseType'),
    flex: 1.5,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'hospitalPreferenceCardId',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnHospitalPreferenceCardId'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'isActive',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnStatus'),
    flex: 1,
    valueGetter: (value) =>
      value
        ? t('pages.entityManagement.preferenceCardsTable.statusActive')
        : t('pages.entityManagement.preferenceCardsTable.statusInactive'),
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'trackedAssetTypes',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnTrackedAssets'),
    flex: 1.5,
    valueGetter: (_value, row) => {
      const types = row.getTrackedAssetTypes().join(', ');
      return types || null;
    },
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'untrackedAssetTypes',
    headerName: t('pages.entityManagement.preferenceCardsTable.columnUntrackedAssets'),
    flex: 1.5,
    valueGetter: (_value, row) => {
      const types = row.getUntrackedAssetTypes().join(', ');
      return types || null;
    },
    renderCell: (params) => renderTableCell(params.value),
  },
];

interface PreferenceCardsTableProps {
  rows: PreferenceCardListItemEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: PreferenceCardListItemEntity) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

export const PreferenceCardsTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
  sortModel,
  onSortModelChange,
}: PreferenceCardsTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = useMemo(() => getColumns(t), [t]);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.preferenceCards'),
      })}
      skeletonColumnCount={6}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      sortingMode="server"
    />
  );
};
